import { useCallback, useEffect, useRef, type FormEvent, type Ref } from 'react';
import { useTranslation } from '../contexts/TranslationContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { GUEST_QUESTION_MAX_LENGTH } from '../services/guestQuestion';
import type { PublicGuestQuestionPhase } from '../hooks/usePublicGuestQuestion';
import { FadeSwitch } from './FadeSwitch';
import { ArrowRight, Loader2, X } from './Icons';
import { BrandButton } from './ui/BrandButton';
import { ActionLabel } from './ui/ActionLabel';
import { LandingGuestQuestionExamples } from './LandingGuestQuestionExamples';
import './LandingGuestQuestionComposer.css';

interface LandingGuestQuestionComposerProps {
  question: string;
  website: string;
  phase: PublicGuestQuestionPhase;
  isBusy: boolean;
  captchaContainerRef: Ref<HTMLDivElement>;
  captchaEnabled: boolean;
  onQuestionChange: (value: string) => void;
  onExampleSelect?: (value: string) => void;
  onWebsiteChange: (value: string) => void;
  onSubmit: () => void;
}

export function LandingGuestQuestionComposer(props: LandingGuestQuestionComposerProps) {
  const { t } = useTranslation();
  const questionRef = useRef<HTMLTextAreaElement>(null);
  const selectionAnimationRef = useRef<Animation | null>(null);
  const shouldReduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const resizeQuestion = useCallback(() => {
    const input = questionRef.current;
    if (!input) return;
    const minimumHeight = Number.parseFloat(window.getComputedStyle(input).minHeight) || 72;
    if (!input.value) {
      input.style.height = `${minimumHeight}px`;
      input.style.overflowY = 'hidden';
      return;
    }
    input.style.height = 'auto';
    input.style.height = `${Math.max(minimumHeight, Math.min(input.scrollHeight, 160))}px`;
    input.style.overflowY = input.scrollHeight > 160 ? 'auto' : 'hidden';
  }, []);

  useEffect(() => { resizeQuestion(); }, [props.question, resizeQuestion]);
  useEffect(() => {
    window.addEventListener('resize', resizeQuestion);
    return () => {
      window.removeEventListener('resize', resizeQuestion);
      selectionAnimationRef.current?.cancel();
    };
  }, [resizeQuestion]);
  useEffect(() => {
    if (shouldReduceMotion) selectionAnimationRef.current?.cancel();
  }, [shouldReduceMotion]);
  const disabled = props.isBusy || props.phase === 'complete';
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    props.onSubmit();
  };
  const statusKey = props.phase === 'verifying' ? 'verifying' : 'loading';
  const selectExample = (question: string) => {
    if (disabled) return;
    selectionAnimationRef.current?.cancel();
    (props.onExampleSelect ?? props.onQuestionChange)(question);
    const input = questionRef.current;
    if (!input) return;
    if (!shouldReduceMotion && typeof input.animate === 'function') {
      selectionAnimationRef.current = input.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 700, easing: 'ease-out',
      });
    }
    input.focus({ preventScroll: true });
  };

  return (
    <form onSubmit={submit} aria-busy={props.isBusy} className="relative min-w-0">
      <div className="pointer-events-none absolute -left-[10000px] top-0 h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor="landing-guest-website">Website</label>
        <input id="landing-guest-website" name="website" type="text" value={props.website} onChange={(event) => props.onWebsiteChange(event.target.value)} autoComplete="off" tabIndex={-1} />
      </div>
      <div className="landing-guest-composer relative rounded-[1.75rem] border-0 p-4 sm:p-5">
        <label id="landing-guest-title" htmlFor="landing-guest-question" className="sr-only">{t('presentation.guestQuestion.label')}</label>
        <div className="relative">
          <textarea
            ref={questionRef}
            id="landing-guest-question"
            value={props.question}
            onChange={(event) => { selectionAnimationRef.current?.cancel(); props.onQuestionChange(event.target.value); }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && !event.nativeEvent.isComposing && !disabled) {
                event.preventDefault();
                props.onSubmit();
              }
            }}
            placeholder={t('presentation.guestQuestion.placeholder')}
            maxLength={GUEST_QUESTION_MAX_LENGTH}
            rows={2}
            disabled={disabled}
            className="block min-h-[56px] max-h-40 sm:min-h-[72px] w-full resize-none rounded-lg border-0 bg-transparent outline-none pl-1 pr-10 py-1.5 text-[13px] leading-[22px] sm:py-2 sm:text-sm sm:leading-6 text-[rgb(var(--theme-text))] placeholder:text-[rgb(var(--theme-muted)/0.8)] disabled:opacity-70"
            aria-keyshortcuts="Control+Enter Meta+Enter"
          />
          <button
            type="button"
            aria-label={t('presentation.guestQuestion.clearQuestion')}
            aria-hidden={!props.question}
            disabled={disabled || !props.question}
            tabIndex={props.question ? 0 : -1}
            onClick={() => {
              selectionAnimationRef.current?.cancel();
              props.onQuestionChange('');
              questionRef.current?.focus({ preventScroll: true });
            }}
            className={`brand-focus-ring absolute right-0 top-1 inline-flex h-8 w-8 items-center justify-center rounded-full text-[rgb(var(--theme-muted))] transition-[opacity,color,background-color] duration-200 hover:bg-[rgb(var(--theme-text)/0.06)] hover:text-[rgb(var(--theme-text))] disabled:cursor-not-allowed motion-reduce:transition-none ${props.question ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-end gap-3">
          <BrandButton type="submit" size="sm" disabled={!props.question.trim() || disabled} className="group min-h-10 w-full sm:min-h-12 max-w-full px-5 sm:w-auto">
            <FadeSwitch viewKey={props.isBusy ? statusKey : props.phase === 'complete' ? 'complete' : 'idle'}>
              <span className="inline-flex items-center gap-2">
                <ActionLabel text={props.isBusy || props.phase === 'complete'
                  ? t(`presentation.guestQuestion.${props.isBusy ? statusKey : 'answered'}`)
                  : t('hero.ctaLabel')} />
                {props.isBusy ? <Loader2 className="h-4 w-4 shrink-0 animate-spin motion-reduce:animate-none" /> : <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1 motion-reduce:transform-none" />}
              </span>
            </FadeSwitch>
          </BrandButton>
        </div>
      </div>
      <LandingGuestQuestionExamples question={props.question} disabled={disabled} onSelect={selectExample} />
      <div aria-hidden={!props.captchaEnabled} className={`grid transition-[grid-template-rows,opacity] duration-300 motion-reduce:transition-none ${props.captchaEnabled ? 'mt-3 grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="min-h-0 overflow-hidden"><div ref={props.captchaContainerRef} className="mx-auto w-fit max-w-full" /></div>
      </div>
    </form>
  );
}
